import {
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  FormCheckbox,
  Button,
} from '../ui';
import type { MessageSchedule } from '../../types/message';
import type { Assistant } from '../../types/assistant';
import type { WhatsAppSession } from '../../types/api';
import type { MessageTemplate } from '../../types/messageTemplate';
import { formatDateTime } from '../../lib/formatters';

interface MessageFormProps {
  message: MessageSchedule | null;
  assistants?: Assistant[];
  sessions?: WhatsAppSession[];
  templates?: MessageTemplate[];
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function MessageForm({
  message,
  assistants,
  sessions,
  templates,
  onSubmit,
  isLoading,
  onClose,
}: MessageFormProps) {
  return (
    <Form onSubmit={onSubmit} className="space-y-4">
      <FormField>
        <FormLabel htmlFor="title">Título</FormLabel>
        <FormInput
          id="title"
          name="title"
          defaultValue={message?.title || ''}
          placeholder="Título da mensagem"
          maxLength={100}
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="message" required>
          Mensagem
        </FormLabel>
        <FormTextarea
          id="message"
          name="message"
          defaultValue={message?.message}
          required
          rows={4}
          placeholder="Digite sua mensagem..."
        />
      </FormField>

      <FormField>
        <FormLabel htmlFor="message_json">
          Dados JSON (Opcional)
        </FormLabel>
        <FormTextarea
          id="message_json"
          name="message_json"
          defaultValue={message?.message_json ? JSON.stringify(message.message_json, null, 2) : ''}
          rows={4}
          placeholder='{"campo1": "valor1", "campo2": "valor2"}'
        />
        <p className="mt-2 text-sm text-gray-500">
          JSON com dados estruturados da mensagem. Deve ser um objeto JSON válido.
        </p>
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="sendtoPhone">Telefone</FormLabel>
          <FormInput
            id="sendtoPhone"
            name="sendtoPhone"
            defaultValue={message?.sendtoPhone || ''}
            placeholder="Ex: 5511999999999"
            maxLength={50}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="sendToMail">Email</FormLabel>
          <FormInput
            id="sendToMail"
            name="sendToMail"
            type="email"
            defaultValue={message?.sendToMail || ''}
            placeholder="Ex: exemplo@email.com"
            maxLength={255}
          />
        </FormField>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="assistant_id">Assistente</FormLabel>
          <FormSelect
            id="assistant_id"
            name="assistant_id"
            defaultValue={message?.assistant_id?.toString() || ''}
          >
            <option value="">Selecione um assistente</option>
            {assistants?.map(assistant => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name}
              </option>
            ))}
          </FormSelect>
        </FormField>

        <FormField>
          <FormLabel htmlFor="whatsapp_id">Conexão WhatsApp</FormLabel>
          <FormSelect
            id="whatsapp_id"
            name="whatsapp_id"
            defaultValue={message?.whatsapp_id?.toString() || ''}
          >
            <option value="">Selecione uma conexão</option>
            {sessions?.map(session => (
              <option key={session.id} value={session.id}>
                {session.session_name}
              </option>
            ))}
          </FormSelect>
        </FormField>
      </div>

      <FormField>
        <FormLabel htmlFor="template_id">Template</FormLabel>
        <FormSelect
          id="template_id"
          name="template_id"
          defaultValue={message?.template_id?.toString() || ''}
        >
          <option value="">Selecione um template</option>
          {templates?.map(template => (
            <option key={template.id} value={template.id}>
              {template.name || template.category_description || `Template ${template.id}`}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField>
          <FormLabel htmlFor="scheduled_at">Agendamento</FormLabel>
          <FormInput
            type="datetime-local"
            id="scheduled_at"
            name="scheduled_at"
            defaultValue={message?.scheduled_at ? formatDateTime(message.scheduled_at, 'iso') : ''}
          />
        </FormField>

        <FormField>
          <FormLabel htmlFor="priority">Prioridade</FormLabel>
          <FormInput
            type="number"
            id="priority"
            name="priority"
            defaultValue={message?.priority || 99}
            min={1}
            max={999}
            placeholder="99"
          />
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Menor = maior prioridade
          </p>
        </FormField>

        <FormField>
          <FormLabel htmlFor="key">Chave</FormLabel>
          <FormInput
            type="number"
            id="key"
            name="key"
            defaultValue={message?.key || ''}
            placeholder="Chave de referência"
          />
        </FormField>
      </div>

      <FormField>
        <div className="flex items-center space-x-2">
          <FormCheckbox
            id="send"
            name="send"
            defaultChecked={message?.send || false}
          />
          <FormLabel htmlFor="send" className="mb-0">
            Marcar como enviado
          </FormLabel>
        </div>
      </FormField>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {message ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default MessageForm;
